// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
/**
 * Industry options for CRM customers, prospects, and contacts — re-exports the
 * SHARED canonical vertical taxonomy (`@xop-any-time/shared`), the ONE source
 * of truth mirrored from the sales agent (`xop-agents/apps/sales/app/services/verticals.py`).
 * Stored as the label string on `customers.industry`.
 *
 * The CRM used to keep its own separate, collapsed list ("Oil & Gas" fused
 * oil+gas, "Power & Utilities" fused power+wind, etc.) that diverged from the
 * agent's roster. That's retired in favor of `VERTICAL_LABELS` so customers,
 * prospects, contacts, the sales agent, and the Email Campaigns audience
 * builder all speak the same vocabulary.
 *
 * AI-generated prospects get this set automatically from the sales agent's
 * `vertical` (mapped in the dashboard's add-to-crm route / LeadPage on
 * "Convert to Prospect"); humans pick it in the prospect edit form. Shared here
 * so the Prospects list (`formatIndustry`) and the edit form (`PROSPECT_INDUSTRIES`)
 * speak one vocabulary.
 */
import { VERTICAL_LABELS, canonicalizeIndustry } from "@xop-any-time/shared"

/**
 * The canonical industry labels, in display order (energy first, Unclassified
 * last) — mirrors the shared `VERTICAL_LABELS`. Re-exported under the original
 * name so existing importers (the prospect form, InlineSelectEditor call
 * sites) keep working unchanged.
 */
export const PROSPECT_INDUSTRIES = VERTICAL_LABELS

/**
 * `{ value, label }` options for the inline-edit / form industry selects. The
 * stored value IS the clean label (no separate code), so value === label.
 */
export const INDUSTRY_OPTIONS: { value: string; label: string }[] = PROSPECT_INDUSTRIES.map((v) => ({
  value: v,
  label: v,
}))

/**
 * Humanize a stored industry for display. Clean labels (with a space or "&")
 * pass through unchanged; a raw agent vertical (snake_case, e.g.
 * "public_safety") becomes "Public Safety" so older / unmapped values still
 * read well in the list.
 */
export function formatIndustry(value: string | null | undefined): string {
  if (!value) return ""
  const v = value.trim()
  if (/[ &]/.test(v)) return v
  return v.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
}

/**
 * Map a free-form sales-agent `vertical` (e.g. "nuclear_power_plants",
 * "public-safety", "oil & gas midstream") onto one of the canonical shared
 * `PROSPECT_INDUSTRIES` labels, used when an operator runs "Convert to
 * Prospect" so AI-generated prospects land with a clean, canonical sector.
 *
 * Delegates to the shared `canonicalizeIndustry()` (the same fold the agent +
 * crm-api backfill use), so unrecognized text becomes "Unclassified" rather
 * than being kept verbatim — the prospect form still lets a human retag it,
 * and a legacy out-of-list stored value is preserved as an extra select
 * option (see `prospect-form-modal.tsx`).
 */
export function industryFromVertical(vertical: string | null | undefined): string {
  if (!vertical) return ""
  const v = vertical.trim()
  if (!v) return ""
  return canonicalizeIndustry(v)
}
